import { createFileRoute, Navigate } from '@tanstack/react-router'
import {
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  FileText,
  Inbox,
  Play,
  RotateCcw,
  Target,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import Shepherd from 'shepherd.js'
import type {
  StepOptions,
  StepOptionsButton,
  StepText,
  StringOrStringFunction,
  Tour,
} from 'shepherd.js'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { demodayOrientationTour } from '@/lib/tours/demoday/orientation'
import { tourDefaultStepOptions } from '@/lib/tours/utils/default-step-options'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/tour-preview')({
  component: TourPreviewPage,
})

const isPreviewEnabled = import.meta.env.DEV || import.meta.env.VERCEL_ENV === 'preview'
const steps = demodayOrientationTour.steps

const stepAnchors: Record<string, string> = {
  'welcome': 'dashboard-greeting',
  'view-accounts': 'home-view-accounts',
  'market-research': 'home-market-research',
  'profile': 'org-profile-card',
  'offering': 'org-offering-card',
  'customers': 'org-customers-card',
  'closing': 'home-view-accounts',
  'inbox': 'sidebar-inbox',
}

const stepSurfaces: Record<string, string> = {
  'welcome': 'Dashboard',
  'view-accounts': 'Dashboard',
  'market-research': 'Dashboard',
  'profile': 'Organization',
  'offering': 'Organization',
  'customers': 'Organization',
  'closing': 'Dashboard',
  'inbox': 'Sidebar',
}

function TourPreviewPage() {
  if (!isPreviewEnabled) {
    return <Navigate to="/" />
  }
  return <TourPreview />
}

function TourPreview() {
  const [activeIndex, setActiveIndex] = useState(0)
  const liveTourRef = useRef<Tour | undefined>(undefined)
  const activeStep = steps[activeIndex] ?? steps[0]
  const activeAnchor = activeStep ? stepAnchors[activeStep.id ?? ''] : undefined

  useEffect(() => {
    return () => {
      liveTourRef.current?.cancel()
      liveTourRef.current = undefined
    }
  }, [])

  const startLiveTour = () => {
    liveTourRef.current?.cancel()

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: tourDefaultStepOptions,
      steps: steps.map((step, index) => ({
        ...step,
        buttons: previewButtons(index, steps.length),
        when: undefined,
      })),
    })

    tour.on('show', ({ step }: { step: { id?: string, el?: HTMLElement } }) => {
      const index = steps.findIndex(candidate => candidate.id === step.id)
      if (index !== -1) {
        setActiveIndex(index)
        injectStepDots(step, index, steps.length)
        decorateStepTitle(step, index, steps.length)
      }
    })
    tour.on('complete', () => {
      liveTourRef.current = undefined
    })
    tour.on('cancel', () => {
      liveTourRef.current = undefined
    })

    liveTourRef.current = tour
    tour.start()
  }

  const stopLiveTour = () => {
    liveTourRef.current?.cancel()
    liveTourRef.current = undefined
  }

  return (
    <div className="h-full overflow-y-auto bg-background text-foreground">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-5 py-5 lg:px-8">
        <header className="flex flex-col gap-4 border-b pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <Badge variant="secondary" className="w-fit">
              Dev preview
            </Badge>
            <div>
              <h1 className="text-3xl font-semibold tracking-normal">
                Demo-day orientation tour
              </h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Static copy review plus a local Shepherd run against fake anchors. This page keeps the onboarding tour visible without login, seeded data, or route hopping.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setActiveIndex(0)}>
              <RotateCcw className="mr-2 h-4 w-4" />
              First step
            </Button>
            <Button variant="outline" onClick={stopLiveTour}>
              Stop
            </Button>
            <Button onClick={startLiveTour}>
              <Play className="mr-2 h-4 w-4" />
              Run live tour
            </Button>
          </div>
        </header>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
          <main className="space-y-5">
            <PreviewStage activeAnchor={activeAnchor} />
            {activeStep && (
              <section className="grid gap-5 lg:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
                <StaticPopover step={activeStep} index={activeIndex} />
                <StepCopyPanel step={activeStep} index={activeIndex} />
              </section>
            )}
          </main>

          <aside className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">Steps</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Select a step to inspect the exact title, body, and target.
              </p>
            </div>
            <div className="space-y-2">
              {steps.map((step, index) => (
                <button
                  key={step.id ?? index}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={cn(
                    'w-full rounded-md border bg-background p-3 text-left transition-colors hover:bg-muted/60',
                    index === activeIndex && 'border-primary bg-primary/5',
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                      Step
                      {' '}
                      {index + 1}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {stepSurfaces[step.id ?? ''] ?? 'Workspace'}
                    </span>
                  </div>
                  <div className="mt-2 text-sm font-medium">
                    {resolveTitle(step.title)}
                  </div>
                  <div className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                    {resolveText(step.text)}
                  </div>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

function PreviewStage({ activeAnchor }: { activeAnchor?: string }) {
  const isActive = (anchor: string) => activeAnchor === anchor

  return (
    <section className="rounded-md border bg-card text-card-foreground shadow-card">
      <div className="grid min-h-136 gap-0 lg:grid-cols-[10rem_minmax(0,1fr)_24rem]">
        <SidebarNavPreview activeAnchor={activeAnchor} />
        <div className="space-y-5 p-5 lg:p-6">
          <div
            data-tour="dashboard-greeting"
            className={cn(
              'rounded-md border bg-background p-4 transition-all',
              isActive('dashboard-greeting') && 'border-primary ring-2 ring-primary/30',
            )}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Generated workspace
                </p>
                <h2 className="mt-1 text-2xl font-semibold tracking-normal">
                  Hi Alex, ready to discover new opportunities today?
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                  Supplyco Scout finds manufacturing prospects and gives your team actionable account context.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <WorkspaceAction
              anchor="home-view-accounts"
              title="View all scored accounts"
              body="Top accounts and buying signals, ranked by fit."
              icon={<Target className="h-5 w-5" />}
              active={isActive('home-view-accounts')}
            />
            <WorkspaceAction
              anchor="home-market-research"
              title="Market research reports"
              body="Segment context before Scout runs or campaign spend."
              icon={<BarChart3 className="h-5 w-5" />}
              active={isActive('home-market-research')}
            />
          </div>

          <div className="rounded-md border bg-background p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold">Scored account queue</h3>
                <p className="text-sm text-muted-foreground">
                  Mock list so the tour has a real dashboard surface to point at.
                </p>
              </div>
              <Badge variant="default">7 ready</Badge>
            </div>
            <div className="mt-4 grid gap-2">
              {['Pioneer Controls', 'Northstar Automation', 'Vector Assembly'].map((name, index) => (
                <div key={name} className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div>
                    <div className="text-sm font-medium">{name}</div>
                    <div className="text-xs text-muted-foreground">
                      Fit score
                      {' '}
                      {94 - index * 4}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t bg-muted/30 p-5 lg:border-l lg:border-t-0 lg:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Organization pages
            </h3>
          </div>
          <div className="space-y-3">
            <OrganizationPreviewCard
              anchor="org-profile-card"
              title="Company profile"
              body="Website, NAICS, geography, and founding details."
              active={isActive('org-profile-card')}
            />
            <OrganizationPreviewCard
              anchor="org-offering-card"
              title="Value proposition"
              body="What the company sells, who it helps, and why buyers care."
              active={isActive('org-offering-card')}
            />
            <OrganizationPreviewCard
              anchor="org-customers-card"
              title="Ideal Customer Profile"
              body="Criteria and scoring anchors used against every prospect."
              active={isActive('org-customers-card')}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

function SidebarNavPreview({ activeAnchor }: { activeAnchor?: string }) {
  const isActive = (anchor: string) => activeAnchor === anchor

  return (
    <div className="border-b bg-muted/40 p-3 lg:border-b-0 lg:border-r lg:p-4">
      <div className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
        Sidebar
      </div>
      <div className="flex flex-row gap-2 lg:flex-col">
        <div
          data-tour="sidebar-inbox"
          className={cn(
            'flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-medium transition-all',
            isActive('sidebar-inbox') && 'border-primary ring-2 ring-primary/30',
          )}
        >
          <Inbox className="h-4 w-4 text-primary" />
          <span>Inbox</span>
        </div>
      </div>
    </div>
  )
}

function WorkspaceAction({
  active,
  anchor,
  body,
  icon,
  title,
}: {
  active: boolean
  anchor: string
  body: string
  icon: ReactNode
  title: string
}) {
  return (
    <div
      data-tour={anchor}
      className={cn(
        'flex min-h-36 flex-col justify-between rounded-md border bg-background p-4 transition-all',
        active && 'border-primary ring-2 ring-primary/30',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          {icon}
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <div>
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="mt-1 text-sm leading-5 text-muted-foreground">{body}</p>
      </div>
    </div>
  )
}

function OrganizationPreviewCard({
  active,
  anchor,
  body,
  title,
}: {
  active: boolean
  anchor: string
  body: string
  title: string
}) {
  return (
    <div
      data-tour={anchor}
      className={cn(
        'rounded-md border bg-background p-4 transition-all',
        active && 'border-primary ring-2 ring-primary/30',
      )}
    >
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
        <FileText className="h-4 w-4" />
      </div>
      <h4 className="text-sm font-semibold">{title}</h4>
      <p className="mt-1 text-sm leading-5 text-muted-foreground">{body}</p>
    </div>
  )
}

function StaticPopover({ index, step }: { index: number, step: StepOptions }) {
  return (
    <div className="shepherd-element relative w-full self-start">
      <div className="shepherd-header">
        <h3 className="shepherd-title">
          <span className="shepherd-step-eyebrow">
            Step
            {' '}
            {index + 1}
            {' '}
            of
            {' '}
            {steps.length}
          </span>
          {resolveTitle(step.title)}
        </h3>
      </div>
      <div className="shepherd-text">{resolveText(step.text)}</div>
      <div className="shepherd-footer">
        <StepDots currentIndex={index} />
        {index > 0 && <span className="shepherd-button shepherd-button-secondary">Back</span>}
        <span className="shepherd-button">
          {index === steps.length - 1 ? 'Finish' : 'Next'}
        </span>
      </div>
    </div>
  )
}

function StepCopyPanel({ index, step }: { index: number, step: StepOptions }) {
  const anchor = stepAnchors[step.id ?? '']

  return (
    <section className="rounded-md border bg-background p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            Copy review
          </p>
          <h2 className="mt-1 text-xl font-semibold tracking-normal">
            {resolveTitle(step.title)}
          </h2>
        </div>
        <Badge variant="secondary">
          {stepSurfaces[step.id ?? ''] ?? 'Workspace'}
        </Badge>
      </div>
      <p className="mt-4 text-sm leading-6 text-muted-foreground">
        {resolveText(step.text)}
      </p>
      <Separator className="my-4" />
      <dl className="grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Step</dt>
          <dd className="mt-1 font-medium">
            {index + 1}
            {' '}
            /
            {' '}
            {steps.length}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Anchor</dt>
          <dd className="mt-1 font-medium">{anchor ?? 'Centered'}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Placement</dt>
          <dd className="mt-1 font-medium">{step.attachTo?.on ?? 'center'}</dd>
        </div>
      </dl>
    </section>
  )
}

function StepDots({ currentIndex }: { currentIndex: number }) {
  return (
    <div className="shepherd-step-dots" aria-label={`Step ${currentIndex + 1} of ${steps.length}`}>
      {steps.map((step, index) => (
        <span
          key={step.id ?? index}
          className={cn(
            'shepherd-step-dot',
            index === currentIndex && 'shepherd-step-dot-active',
          )}
        />
      ))}
    </div>
  )
}

function previewButtons(index: number, total: number): StepOptionsButton[] {
  const buttons: StepOptionsButton[] = []
  if (index > 0) {
    buttons.push({
      text: 'Back',
      classes: 'shepherd-button-secondary',
      action() {
        this.back()
      },
    })
  }
  buttons.push({
    text: index === total - 1 ? 'Finish' : 'Next',
    action() {
      if (index === total - 1) {
        this.complete()
        return
      }
      this.next()
    },
  })
  return buttons
}

function injectStepDots(
  ctx: { el?: HTMLElement },
  currentIndex: number,
  total: number,
) {
  if (total < 2 || !ctx.el) {
    return
  }
  const footer = ctx.el.querySelector<HTMLDivElement>('.shepherd-footer')
  if (!footer || footer.querySelector('.shepherd-step-dots')) {
    return
  }
  const dots = document.createElement('div')
  dots.className = 'shepherd-step-dots'
  dots.setAttribute('aria-label', `Step ${currentIndex + 1} of ${total}`)
  for (let i = 0; i < total; i += 1) {
    const dot = document.createElement('span')
    dot.className = `shepherd-step-dot${i === currentIndex ? ' shepherd-step-dot-active' : ''}`
    dots.append(dot)
  }
  footer.insertBefore(dots, footer.firstChild)
}

function decorateStepTitle(
  ctx: { el?: HTMLElement },
  currentIndex: number,
  total: number,
) {
  if (total < 2 || !ctx.el) {
    return
  }
  const title = ctx.el.querySelector<HTMLHeadingElement>('.shepherd-title')
  if (!title || title.querySelector('.shepherd-step-eyebrow')) {
    return
  }
  const eyebrow = document.createElement('span')
  eyebrow.className = 'shepherd-step-eyebrow'
  eyebrow.textContent = `Step ${currentIndex + 1} of ${total}`
  title.insertBefore(eyebrow, title.firstChild)
}

function resolveTitle(title: StringOrStringFunction | undefined): string {
  if (typeof title === 'function') {
    return title()
  }
  return title ?? 'Untitled step'
}

function resolveText(text: StepText | undefined): string {
  const resolved = typeof text === 'function' ? text() : text
  if (isStringArray(resolved)) {
    return resolved.join(' ')
  }
  if (typeof resolved === 'string') {
    return resolved
  }
  return resolved?.textContent ?? ''
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value)
}
